import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import _, { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import { useHistory, useParams } from 'react-router-dom'
import Blockies from 'react-blockies'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'

import useModal from 'hooks/useModal'

import { IoEye, IoPencil, IoSearch, IoTrashBin, IoClose, IoAdd } from 'react-icons/io5'

import AddBlackList from './modals/AddUserModal'
import notify from 'components/atoms/Notify'
import apiDelete from 'lib/network/apiDelete'
import PageFooter from 'components/paginationComponent'
import { useSelector } from 'react-redux'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

const BlackList = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [fraudUsers, setFraudUsers] = useState([])
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')

  const [selectedFraudUserId, setSelectedFraudUserId] = useState(null)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({})

  const { id } = useParams()

  useEffect(() => {
    fetchBlackList()
  }, [search, sort])

  const fetchBlackList = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }
    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    params.query = search || undefined
    params.sortBy = sort.sortBy || undefined
    params.sortOrder = sort.sortOrder || undefined

    const response = await apiGet(`fraud-users`, params)
    setFraudUsers(page === 1 ? response.data.fruadUser : [...fraudUsers, ...response.data.fruadUser])

    setPagination(response?.data?.pagination || {})
    setLoading(false)
  }

  const handleAddFraudUserSuccess = userId => {
    closeModal()
    fetchBlackList()
  }

  const deleteFraudUser = id => {
    notify.deleteConfirm({
      title: t('blacklist.modals.deleteFraudUser'),
      message: t('blacklist.modals.deleteConfirm'),
      onConfirm: async () => {
        const response = await apiDelete(`fraud-users/${id}`)
        if (response?.success) {
          fetchBlackList()
          notify.success(t('blacklist.messages.fraudUserDeletedSuccess'))
        }
      },
    })
  }
  const isNonAdminView = selectedFraudUserId && !hasAdminAccess

  const [AddUserModal, openAddFraudUserModel, closeModal] = useModal({
    content: <AddBlackList onSuccess={handleAddFraudUserSuccess} id={selectedFraudUserId} />,
    title: t('blacklist.modals.addBlacklist'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })
  const columns = [
    {
      name: t('blacklist.columns.userDetail'),
      width: '10%',
      key: 'userDetail',
      sortable: true,
      rendered: item => {
        return (
          <div className="flex flex-row items-center cursor-pointer">
            <div className="flex-shrink-0 h-10 w-10 relative">
              {item?.user?.avatar ? (
                <img className="h-10 w-10 rounded-full" src={item?.user?.avatar} alt="" />
              ) : (
                <Blockies className="rounded-full" seed={item.walletAddress || item.email || ''} size={6.5} scale={6} />
              )}
            </div>
            <div className={isRTL ? 'mr-4' : 'ml-4'}>
              <div className="text-sm text-normal text-gray-900">{item?.user?.fullName}</div>
              <div className="text-sm text-normal text-gray-900">{item?.user?.email}</div>
              <div className="text-sm text-normal text-gray-900">{item?.user?.phone}</div>
            </div>
          </div>
        )
      },
    },
    {
      width: '10%',
      name: t('blacklist.columns.fraudName'),
      key: 'name',
      sortable: true,
      rendered: item => {
        const name = item.name

        return (
          <>
            <div>
              <span className="text-[13px]">{name}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('blacklist.columns.phoneNumber'),
      key: 'phone',
      sortable: true,
      rendered: item => {
        const phone = item.phone

        return (
          <>
            <div>
              <span className="text-[13px]">{phone}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('blacklist.columns.fraudCivilId'),
      key: 'civilId',
      sortable: true,
      rendered: item => {
        const civilId = item.civilId
        return (
          <>
            <div>
              <span className="text-[13px]">{civilId}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('blacklist.columns.createdByUser'),
      key: 'createdByUser',
      sortable: true,
      rendered: item => {
        const createdByUser = item?.createdByUser?.fullName

        return (
          <>
            <div>
              <span className="text-[13px]">{createdByUser}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('blacklist.columns.createdByCompany'),
      key: 'createdByCompany',
      sortable: true,
      rendered: item => {
        const createdByCompany = item?.createdByCompany?.name

        return (
          <>
            <div>
              <span className="text-[13px]">{createdByCompany}</span>
            </div>
          </>
        )
      },
    },

    {
      width: '5%',
      name: t('blacklist.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'
        return createdAt
      },
    },
    {
      width: '5%',
      name: t('blacklist.columns.actions'),
      key: 'actions',
      rendered: item => (
        <div className="flex space-x-2 sm:space-x-4">
          {hasAdminAccess ? (
            <IoPencil
              className="text-dark-600 cursor-pointer h-4 w-4 sm:h-5 sm:w-5"
              onClick={() => {
                setSelectedFraudUserId(item._id)
                openAddFraudUserModel()
              }}
            />
          ) : (
            <IoEye
              className="text-dark-600 cursor-pointer h-4 w-4 sm:h-5 sm:w-5"
              onClick={() => {
                setSelectedFraudUserId(item._id)
                openAddFraudUserModel()
              }}
            />
          )}
          {hasAdminAccess && <IoTrashBin className="text-red-500 cursor-pointer h-4 w-4 sm:h-5 sm:w-5" onClick={() => deleteFraudUser(item._id)} />}
        </div>
      ),
    },
  ]

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('blacklist.breadcrumb'), pathname: '/fraud-users' }]}
      headerRight={
        <div className="w-full">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-end gap-4">
            <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className={`block w-full ${isRTL ? 'pr-12 pl-3' : 'pl-9 pr-3'} py-2 shadow-none text-sm bg-gray-100 border-0 focus:outline-none rounded-sm`}
                dir={isRTL ? 'rtl' : 'ltr'}
                placeholder={t('blacklist.messages.searchPlaceholder')}
              />
            </div>

            <div className="flex-shrink-0">
              <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            </div>

            <div className="flex-shrink-0">
              <Button
                onClick={() => {
                  setSelectedFraudUserId(null)
                  openAddFraudUserModel()
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-500 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 transition-all"
              >
                <IoAdd className="h-4 w-4 mr-2" />
                {t('blacklist.buttons.addBlacklist')}
              </Button>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-4 p-4 bg-gray-50 rounded-xl shadow-sm">
            {/* Search Row */}
            <div className="flex items-center gap-2">
              {/* Search Bar */}
              <div className="relative flex-1">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                  <IoSearch className={`h-5 w-5 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                </div>
                <input
                  onChange={e => setSearch(e.target.value)}
                  value={search}
                  type="text"
                  name="search"
                  id="search"
                  className={`block w-full ${
                    isRTL ? 'pr-12 pl-9' : 'pl-10 pr-9'
                  } py-2 text-sm rounded-md border border-gray-200 bg-white shadow-sm placeholder:opacity-0 xs:placeholder:opacity-100 xs:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={t('blacklist.messages.searchPlaceholder')}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-2' : 'right-0 pr-2'} flex items-center text-gray-400 hover:text-red-500`}
                  >
                    <IoClose className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Add Button */}
            <div>
              <Button
                onClick={() => {
                  setSelectedFraudUserId(null)
                  openAddFraudUserModel()
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-md"
              >
                <IoAdd className="h-4 w-4" />
                {t('blacklist.buttons.addBlacklist')}
              </Button>
            </div>

            {/* Pagination */}
            <div className="flex justify-center">
              <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            </div>
          </div>
        </div>
      }
      title={t('blacklist.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {fraudUsers?.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('blacklist.messages.noBlacklistedUsersFound')}</h3>
                        <p className="text-gray-500">{t('blacklist.messages.noBlacklistedUsersDescription')}</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table
                          hasMore={pagination?.currentPage !== pagination?.totalPages}
                          fetchData={() => fetchBlackList(pagination?.currentPage + 1)}
                          sortBy={sort.sortBy}
                          sortOrder={sort.sortOrder}
                          handleSort={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })}
                          data={fraudUsers}
                          columns={columns}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <AddUserModal />
    </Layout>
  )
}

export default BlackList
